//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import { mapState } from 'vuex';
import { PromiseTracker } from '@/helpers/util';
import { ACCOUNT } from '@/store/types/namespaces';
import DpWidgetLoader from '../loader/WidgetLoader.vue';
import DpAlert from '../notification/Alert.vue';
import TreeViewHeader from './TreeViewHeader.vue';
import TreeViewRows from './TreeViewRows.vue';
import { fetch, fetchKpi, filterRowData, findInTree, modelDataToRows } from './helpers';

// No point in making this reactive
let nodeId = 0;

const defaultConfig = {
  columns: [
    { type: 'identifier', property: '$id' },
  ],
};

const States = {
  INIT: 0,
  LOADING: 1,
  ERROR: 2,
  READY: 3,
};

export default {
  name: 'DpTreeView',
  components: {
    DpAlert,
    DpWidgetLoader,
    TreeViewHeader,
    TreeViewRows,
  },
  props: {
    kpi: { type: Object, default: null },
    config: { type: Object, default: () => defaultConfig },
    range: { type: Object, default: null },
    period: { type: Number, default: 0 },
    offset: { type: Number, default: 0 },
    scope: { type: String, default: null },
    selection: { type: Array, default: () => [] },
    filterText: { type: String, default: null },
    kpiFilters: { type: Object, default: () => ({}) },
    disableHierarchy: { type: Boolean, default: false },
    locked: { type: Boolean, default: false },
    singleSelection: { type: Boolean, default: false },
  },
  data() {
    return {
      tableRows: [],
      parsedConfig: {},
      promiseTracker: new PromiseTracker(),
      currentState: States.INIT,
      States,
    };
  },
  computed: {
    ...mapState(ACCOUNT, { accountId: 'id' }),
    tableRowsFiltered() {
      const {
        filterText,
        kpiFilters,
        tableRows,
        kpi,
        columns,
      } = this;
      return filterRowData({
        tableRows,
        kpiFilters,
        filterText,
        kpi,
        columns,
      });
    },
    columns() {
      if (!this.parsedConfig.columns) return [];

      const cols = this.parsedConfig.columns.map((c) => {
        const column = {
          ...c,
        };
        if (column.type === 'kpi') {
          column.label = c.kpi;
        } else if (!column.label) {
          column.label = c.property;
        }

        if (!column.width || column.width === 'auto') {
          column.width = null;
        }

        return column;
      });
      return cols;
    },
    isKpiGrouped() {
      const { kpi } = this;
      return kpi && kpi.grouping && kpi.grouping.name !== '$id';
    },
  },
  watch: {
    // Watch for any config changes
    scope() { this.update(); },
    period() { this.update(); },
    range() { this.update(); },
    offset() { this.update(); },
    disableHierarchy() {
      this.parseConfig();
      this.update();
    },
    kpi: {
      deep: true,
      handler(newKpi, oldKpi) {
        // Catch the case of the KPI not actually changing, but this watcher triggering
        if (newKpi && oldKpi && newKpi.$id === oldKpi.$id) return;
        this.parseConfig();
        this.update();
      },
    },
    config: {
      deep: true,
      handler() {
        this.parseConfig();
      },
    },
    tableRowsFiltered(rows) {
      const { kpi, disableHierarchy } = this;
      const grouping = kpi && kpi.grouping ? kpi.grouping.name : null;
      if (grouping && !disableHierarchy) {
        this.$emit('device-number-change', null);
        return;
      }
      this.$emit('device-number-change', rows.length);
    },
  },
  mounted() {
    // This one is important to make not reactive, otherwise we create _lots_ of observers
    this.modelData = [];
    this.parseConfig();
    this.update();
  },
  methods: {
    parseConfig() {
      // Do a raw object clone to break observer link
      const config = this.config ? JSON.parse(JSON.stringify(this.config)) : defaultConfig;
      const parsedConfig = {
        columns: [],
      };

      const { kpi } = this;

      const columnMap = (col) => {
        const column = col;
        if (!column.sort) {
          column.sort = null;
        }
        if (!column.width) {
          column.width = null;
        }

        // Force $id to be identifier
        if (column.property === '$id') {
          column.type = 'identifier';

          const grouping = this.isKpiGrouped ? kpi.grouping.name : null;
          column.label = grouping && !this.disableHierarchy ? `${grouping}/$id` : '$id';
        }

        if (kpi) {
          const { name, measurements } = kpi;
          if (column.type === 'kpi' && name !== column.kpi && measurements.length === 1) {
            column.kpi = null;
            column.label = null;
          }
        }

        return column;
      };

      if (config.columns) {
        parsedConfig.columns = config.columns.map(columnMap).filter(({ label }) => label !== null);
      }

      if (!kpi) {
        parsedConfig.columns = parsedConfig.columns.filter((col) => col.type !== 'kpi');
      }

      this.$set(this, 'parsedConfig', parsedConfig);

      const findKpiColumn = (name) => this.parsedConfig.columns.find(
        (c) => c.type === 'kpi' && c.kpi === name,
      );

      // Add KPI column if one does not already exist
      if (kpi) {
        // addColumn relies on parsedConfig existing first
        const { measurements } = kpi;
        if (measurements.length === 1) {
          const { name } = kpi;
          if (!findKpiColumn(name)) {
            // Remove all other kpi columns before adding new one
            parsedConfig.columns = parsedConfig.columns.filter((col) => col.type !== 'kpi');
            this.addColumn({ type: 'kpi', label: name, field: name }, false);
          }
        } else {
          // Remove any other KPI column
          parsedConfig.columns = parsedConfig.columns.filter((col) => {
            const measurementNames = measurements.map((m) => m.label);
            return col.type !== 'kpi' || measurementNames.includes(col.kpi);
          });

          measurements.forEach((m) => {
            // Only add column if one doesn't currently exist
            const { label } = m;
            if (!findKpiColumn(label)) {
              this.addColumn({ type: 'kpi', label, field: label }, false);
            }
          });
        }
      }
    },
    async update(node) {
      const {
        kpi,
        columns,
        accountId,
        period,
        range,
        offset,
        scope,
        disableHierarchy,
      } = this;

      const grouping = this.isKpiGrouped ? kpi.grouping.name : null;

      // If this is a base level update then remove all old data
      if (!node) {
        this.modelData = [];
        this.updateTableRows();
        this.currentState = States.LOADING;
      }

      let data;
      // Only run the most recent query if it's at the top level, or let queries run async if child
      try {
        const fetchPromise = fetch({
          kpi,
          scope,
          columns,
          accountId,
          period,
          range,
          offset,
          disableHierarchy: grouping ? disableHierarchy : true,
          rootNode: node,
        });

        if (!node) {
          this.promiseTracker.push(fetchPromise);
          data = await this.promiseTracker.runMostRecent(fetchPromise);
        } else {
          data = await fetchPromise;
        }
      } catch (err) {
        this.currentState = States.ERROR;
        return;
      }

      this.currentState = States.READY;

      if (!node) {
        this.modelData = data.map((r) => {
          const drilldown = grouping && grouping !== '$id'
            ? `\"{${grouping}}\" == "${r.$id.replace(/"/g, '\\"')}"` // eslint-disable-line no-useless-escape
            : null;
          nodeId += 1;
          return {
            ...r,
            $model: kpi ? kpi.name : null,
            $children: null,
            $nodeId: nodeId,
            $hasChildren: !disableHierarchy && grouping && grouping !== '$id',
            $drilldown: drilldown,
          };
        });

        this.updateTableRows();

        // Run top-level KPIs
        if (!grouping || disableHierarchy) {
          this.updateKpis(data);
        }

        return;
      }

      const updateNode = findInTree(
        this.modelData,
        (m) => m.$nodeId === node.$nodeId,
      );
      if (!updateNode) return;

      if (data.length) {
        updateNode.$children = data.map((r) => {
          nodeId += 1;
          return {
            ...r,
            $nodeId: nodeId,
          };
        });
        this.updateTableRows();
        this.updateKpis(data, updateNode);
        return;
      }

      // Add a placeholder row with no data
      nodeId += 1;
      updateNode.$children = [{
        $id: `placeholder/${nodeId}`,
        $placeholder: 'No devices currently match the scope',
        $nodeId: nodeId,
      }];
      this.updateTableRows();
    },
    async updateKpis(data, node) {
      if (!this.kpi) return;

      const {
        period,
        offset,
        range,
        accountId,
      } = this;
      const kpis = [this.kpi];

      try {
        kpis.forEach(async (kpi) => {
          let scope = null;
          if (node && node.$drilldown) {
            scope = node.$drilldown;
          }
          const kpiData = await fetchKpi({
            kpi,
            accountId,
            scope,
            period,
            range,
            offset,
          });

          const kpiMap = (d) => {
            const fullData = d;
            const deviceKpis = kpiData[d.$id];
            if (!deviceKpis) {
              const { measurements } = kpi;
              if (measurements.length === 1) {
                this.$set(fullData, kpi.name, { empty: true });
              } else {
                measurements.forEach((m) => {
                  this.$set(fullData, m.label, { empty: true });
                });
              }
              return fullData;
            }

            deviceKpis.forEach((kpiVal) => {
              this.$set(fullData, kpiVal?.label || kpi.name, kpiVal || { empty: true });
            });
            return fullData;
          };

          // Update top level rows with KPI results
          if (!node) {
            this.modelData = this.modelData.map(kpiMap);
            this.updateTableRows();
            return;
          }

          // Update child rows with KPI results
          const updateNode = findInTree(
            this.modelData,
            (m) => m.$nodeId === node.$nodeId,
          );
          if (!updateNode || !updateNode.$children) return;
          updateNode.$children = updateNode.$children.map(kpiMap);
          this.updateTableRows();
        });
      } catch (err) {
        this.currentState = States.ERROR;
      }
    },
    updateTableRows() {
      const { columns } = this;
      if (!this.modelData || !Array.isArray(this.modelData)) {
        this.tableRows = [];
        return;
      }

      this.tableRows = modelDataToRows({
        columns,
        data: this.modelData,
      });
    },
    selectionChange(devices) {
      this.$emit('change-selection', devices);
    },
    contract(node) {
      if (!node) return;

      const updateNode = findInTree(
        this.modelData,
        (m) => m.$nodeId === node.$tableMeta.nodeId,
      );
      if (!updateNode) return;
      updateNode.$expanded = false;
      updateNode.$children = null;
      this.updateTableRows();
    },
    moveColumn({ from, to }) {
      const { columns } = this.parsedConfig;
      let oldIndex = from;
      let newIndex = to;

      while (oldIndex < 0) {
        oldIndex += columns.length;
      }
      while (newIndex < 0) {
        newIndex += columns.length;
      }
      if (newIndex >= columns.length) {
        let k = newIndex - columns.length + 1;
        while (k) {
          columns.push(undefined);
          k -= 1;
        }
      }
      columns.splice(newIndex, 0, columns.splice(oldIndex, 1)[0]);
      this.pushChangedSettings();
    },
    resizeColumn({ columnIndex, width }) {
      const column = this.parsedConfig.columns[columnIndex];
      if (column) {
        column.width = width;
      }
      this.pushChangedSettings();
    },
    removeColumn(columnIndex) {
      this.parsedConfig.columns.splice(columnIndex, 1);
      this.pushChangedSettings();
    },
    addColumn({
      type,
      field,
      label,
      date,
    }, saveSettings = true) {
      const columnObject = {
        label,
        type,
        date,
        sort: null,
        width: null,
      };
      if (type === 'kpi') {
        columnObject.kpi = field;
      } else if (type === 'data') {
        columnObject.property = field;
      } else {
        return;
      }

      this.parsedConfig.columns.push(columnObject);
      this.updateTableRows();

      if (saveSettings) {
        this.pushChangedSettings();
      }
    },
    changeSort({ columnIndex, order }) {
      this.parsedConfig.columns.forEach((col, index) => {
        const column = col;

        if (index === columnIndex) {
          column.sort = order;
        } else {
          column.sort = null;
        }

        this.$set(this.parsedConfig.columns, index, column);
      });
      this.updateTableRows();
      this.pushChangedSettings();
    },
    pushChangedSettings() {
      if (this.locked) return;
      this.$emit('change-settings', this.parsedConfig);
    },
  },
};
